import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { startOfDay, endOfDay, subDays } from "date-fns";
import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useHistory, useParams } from "react-router";
import { getConnectionStatus, getWearingStatus } from "../utils";
import HeartRateCard from "./HeartRateCard";
import HeartRateChart from "./HeartRateChart";
import HRVCard from "./HRVCard";
import HRVChart from "./HRVChart";
import SpO2Card from "./SpO2Card";
import SpO2Chart from "./SpO2Chart";
import TemperatureCard from "./TemperatureCard";
import TemperatureChart from "./TemperatureChart";
import { usePagination, useOuraRealtime } from "components/hooks";
import BatteryCharging from "components/icons/BatteryCharging";
import BatteryFull from "components/icons/BatteryFull";
import BatteryHigh from "components/icons/BatteryHigh";
import BatteryLow from "components/icons/BatteryLow";
import BatteryMedium from "components/icons/BatteryMedium";
import Heart from "components/icons/Heart";
import MainContentContainer from "components/Layouts/MainContentContainer";
import StelDataGrid from "components/StelDataGrid";
import { get, post } from "utils/io";

const TABS = [
  { label: "Heart Rate" },
  { label: "SpO2" },
  { label: "Temperature" },
  { label: "HRV" },
  { label: "Raw data" },
];

const LIMITDEFAULT = 200;

const BATTERY_THRESHOLDS = {
  HIGH: 80,
  MEDIUM: 50,
  LOW: 20,
};

const getBatteryConfig = (batteryLevel, isCharging) => {
  if (isCharging) {
    return {
      background: "#D4941580",
      borderColor: "#F4E6D0",
      icon: <BatteryCharging />,
    };
  }

  const configs = {
    full: {
      background: "#26E21F80",
      borderColor: "#E0F4EA",
      icon: <BatteryFull />,
    },
    high: {
      background: "#26E21F80",
      borderColor: "#E0F4EA",
      icon: <BatteryHigh />,
    },
    medium: {
      background: "#26E21F80",
      borderColor: "#E0F4EA",
      icon: <BatteryMedium />,
    },
    low: {
      background: "#E21F1F80",
      borderColor: "#F4D0D0",
      icon: <BatteryLow />,
    },
  };

  if (batteryLevel > BATTERY_THRESHOLDS.HIGH) return configs.full;
  if (batteryLevel > BATTERY_THRESHOLDS.MEDIUM) return configs.high;
  if (batteryLevel > BATTERY_THRESHOLDS.LOW) return configs.medium;
  return configs.low;
};

const getConnectionStatusDisplay = (connectionStatus) => {
  if (!connectionStatus) return "-";

  const {
    is_bound: isBound,
    hub_connected: hubConnected,
    device_connected: deviceConnected,
    status,
  } = connectionStatus;

  if (!isBound) return "Not Bound";
  if (!hubConnected) return "Hub Disconnected";
  if (deviceConnected === false) return "Device Disconnected";
  if (deviceConnected === true && hubConnected) return "Connected";

  const statusMap = {
    connected: "Connected",
    disconnected: "Disconnected",
    not_bound: "Not Bound",
    hub_connected_device_disconnected: "Device Disconnected",
  };

  return statusMap[status] || status || "-";
};

const getDeviceInfo = (device) => [
  { label: "Mac Address", value: device.mac_address || "-" },
  { label: "Serial Number", value: device.serial_number || "-" },
  { label: "Wear status", value: device.wear_status || "-" },
  {
    label: "Status",
    value: getConnectionStatusDisplay(device.connection_status),
  },
  { label: "Bound Hub ID", value: device.bound_hub || "-" },
];

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function DeviceHeader({
  device,
  isStatusStale = false,
  isRefreshingStatus = false,
}) {
  const batteryConfig = getBatteryConfig(
    device.battery_level,
    device.wear_status === "Charging"
  );
  const deviceInfo = getDeviceInfo(device);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "86px 1fr" },
        gap: 2,
        border: "1px solid #E6E6EC",
        p: 2,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          height: 86,
          width: 86,
          borderRadius: 1,
          position: "relative",
          mx: { xs: "auto", md: 0 },
        }}
      >
        <Box
          sx={{
            border: `1.5px solid ${batteryConfig.borderColor}`,
            borderRadius: "6px",
            p: "2px 4px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: batteryConfig.background,
            position: "absolute",
            bottom: 0,
            width: "-webkit-fill-available",
            gap: "4px",
          }}
        >
          {batteryConfig.icon}
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>
            {device.battery_level}%
          </Typography>
        </Box>
        {isRefreshingStatus && (
          <Chip
            size="small"
            label="Refreshing..."
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              backgroundColor: "#1F79E20D",
              color: "#1F79E2",
              border: "1px solid #1F79E226",
              fontSize: 10,
            }}
          />
        )}
        {isStatusStale && !isRefreshingStatus && (
          <Chip
            size="small"
            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
            label="Last known"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              backgroundColor: "#FF95000D",
              color: "#FF9500",
              border: "1px solid #FF950026",
              fontSize: 10,
            }}
          />
        )}
        <img
          src={device?.model_image_url?.url}
          alt="Device"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 6,
          }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          gap: 2,
          width: "100%",
        }}
      >
        {deviceInfo.map((info) => {
          const isMac = info.label === "Mac Address";
          return (
            <Box
              key={info.label}
              sx={{
                background: "#EEF3F6",
                p: 2,
                borderRadius: 3,
                gridColumn: { xs: isMac ? "1 / -1" : "auto", md: "auto" },
              }}
            >
              <Typography
                sx={{ fontWeight: 500, fontSize: 14, color: "#667085", mb: 1 }}
              >
                {info.label}
              </Typography>
              <Typography
                sx={{ fontWeight: 600, fontSize: 18, color: "#000000DE" }}
              >
                {info.value}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

DeviceHeader.propTypes = {
  device: PropTypes.shape({
    battery_level: PropTypes.number,
    is_charging: PropTypes.bool,
    mac_address: PropTypes.string,
    model_image_url: PropTypes.string,
    serial_number: PropTypes.string,
    status: PropTypes.string,
    wear_status: PropTypes.string,
    connection_status: PropTypes.shape({
      is_bound: PropTypes.bool,
      hub_connected: PropTypes.bool,
      device_connected: PropTypes.bool,
      status: PropTypes.string,
      hub_id: PropTypes.string,
    }),
  }).isRequired,
  isStatusStale: PropTypes.bool,
  isRefreshingStatus: PropTypes.bool,
};

DeviceHeader.defaultProps = {
  isStatusStale: false,
  isRefreshingStatus: false,
};

function MetricsSection({ setIsSyncing, lastMeasures, isSyncing, hubEsn }) {
  const [isLoadingStop, setIsLoadingStop] = useState(false);

  const onSyncMeasure = async () => {
    if (!lastMeasures?.id) return;
    const res = await post("/oura/hr/start", {
      device_id: lastMeasures.id,
    });

    if (res.status === 200) {
      enqueueSnackbar("Syncing measurements started", { variant: "success" });
      setIsSyncing(true);
    } else {
      enqueueSnackbar("Unable to sync measurements", { variant: "error" });
    }
  };

  const onUnsyncMeasure = async () => {
    setIsLoadingStop(true);
    const res = await post("oura/hr/stop", {
      hub_esn: hubEsn,
    });
    if (res.status !== 200) {
      enqueueSnackbar("Unable to unsync hub device", { variant: "error" });
    } else {
      setIsSyncing(false);
      enqueueSnackbar("Hub device unsync initiated successfully", {
        variant: "success",
      });
    }
    setIsLoadingStop(false);
  };

  return (
    <Stack spacing={2} mb={3}>
      <HeartRateCard
        lastMeasures={lastMeasures}
        onSyncMeasure={onSyncMeasure}
        onUnsyncMeasure={onUnsyncMeasure}
        isLoading={isSyncing && isLoadingStop}
        isSyncing={isSyncing}
      />
      <SpO2Card
        lastMeasures={lastMeasures}
        isLoading={isSyncing && isLoadingStop}
      />
      <TemperatureCard
        lastMeasures={lastMeasures}
        isLoading={isSyncing && isLoadingStop}
      />
      <HRVCard
        lastMeasures={lastMeasures}
        isLoading={isSyncing && isLoadingStop}
      />
    </Stack>
  );
}

MetricsSection.propTypes = {
  setIsSyncing: PropTypes.func.isRequired,
  isSyncing: PropTypes.bool,
  lastMeasures: PropTypes.shape({
    id: PropTypes.string,
    last_measure: PropTypes.shape({
      bpm: PropTypes.number,
      spo2: PropTypes.number,
      temp: PropTypes.number,
    }),
    last_used_time: PropTypes.string,
    hr_statistics: PropTypes.shape({
      current: PropTypes.number,
      max: PropTypes.number,
      min: PropTypes.number,
      last_sync: PropTypes.string,
    }),
  }),
  hubEsn: PropTypes.string.isRequired,
};

MetricsSection.defaultProps = {
  isSyncing: false,
  lastMeasures: null,
};

function HeartRateChartView({
  heartRateDevices,
  rangeType,
  setRangeType,
  isLoading,
}) {
  const hasData = heartRateDevices?.data_points?.length > 0;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasData) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            margin: "30px 0",
            py: 4,
          }}
        >
          <Typography color="text.secondary">No Heart Rate Data</Typography>
        </Box>
      );
    }

    return <HeartRateChart heartRateDevices={heartRateDevices} />;
  };

  const handleRangeChange = (_, newValue) => {
    if (newValue) {
      setRangeType(newValue);
    }
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" component="h3">
            Heart Rate
          </Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={rangeType}
            onChange={handleRangeChange}
            sx={{
              "& .MuiToggleButton-root": {
                fontSize: 10,
                px: 1,
                py: 0.5,
                minHeight: 28,
                color: "#666",
                border: "1px solid #E0E0E0",
                "&.Mui-selected": {
                  color: "#1976d2",
                  backgroundColor: "#E3F2FD",
                },
                "&.Mui-focusVisible": {
                  color: "#1976d2",
                },
              },
            }}
          >
            <ToggleButton value="hour">Hour</ToggleButton>
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {renderContent()}
      </Stack>
    </Stack>
  );
}

HeartRateChartView.propTypes = {
  heartRateDevices: PropTypes.shape({
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        timestamp: PropTypes.string,
        open: PropTypes.number,
        close: PropTypes.number,
        min: PropTypes.number,
        max: PropTypes.number,
        trend: PropTypes.string,
      })
    ),
  }),
  rangeType: PropTypes.string.isRequired,
  setRangeType: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

HeartRateChartView.defaultProps = {
  heartRateDevices: null,
};

function SpO2ChartView({ spo2Data, rangeType, setRangeType, isLoading }) {
  const hasData = spo2Data?.data_points?.length > 0;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasData) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            margin: "30px 0",
            py: 4,
          }}
        >
          <Typography color="text.secondary">No SpO2 Data</Typography>
        </Box>
      );
    }

    return <SpO2Chart spo2Data={spo2Data} />;
  };

  const handleRangeChange = (_, newValue) => {
    if (newValue) {
      setRangeType(newValue);
    }
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" component="h3">
            SpO2
          </Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={rangeType}
            onChange={handleRangeChange}
            sx={{
              "& .MuiToggleButton-root": {
                fontSize: 10,
                px: 1,
                py: 0.5,
                minHeight: 28,
                color: "#666",
                border: "1px solid #E0E0E0",
                "&.Mui-selected": {
                  color: "#1976d2",
                  backgroundColor: "#E3F2FD",
                },
                "&.Mui-focusVisible": {
                  color: "#1976d2",
                },
              },
            }}
          >
            <ToggleButton value="hour">Hour</ToggleButton>
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {renderContent()}
      </Stack>
    </Stack>
  );
}

SpO2ChartView.propTypes = {
  spo2Data: PropTypes.shape({
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        timestamp: PropTypes.string,
        open: PropTypes.number,
        close: PropTypes.number,
        min: PropTypes.number,
        max: PropTypes.number,
        trend: PropTypes.string,
      })
    ),
  }),
  rangeType: PropTypes.string.isRequired,
  setRangeType: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

SpO2ChartView.defaultProps = {
  spo2Data: null,
};

function TemperatureChartView({
  temperatureData,
  rangeType,
  setRangeType,
  isLoading,
}) {
  const hasData = temperatureData?.data_points?.length > 0;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasData) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            margin: "30px 0",
            py: 4,
          }}
        >
          <Typography color="text.secondary">No Temperature Data</Typography>
        </Box>
      );
    }

    return <TemperatureChart temperatureData={temperatureData} />;
  };

  const handleRangeChange = (_, newValue) => {
    if (newValue) {
      setRangeType(newValue);
    }
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" component="h3">
            Temperature
          </Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={rangeType}
            onChange={handleRangeChange}
            sx={{
              "& .MuiToggleButton-root": {
                fontSize: 10,
                px: 1,
                py: 0.5,
                minHeight: 28,
                color: "#666",
                border: "1px solid #E0E0E0",
                "&.Mui-selected": {
                  color: "#1976d2",
                  backgroundColor: "#E3F2FD",
                },
                "&.Mui-focusVisible": {
                  color: "#1976d2",
                },
              },
            }}
          >
            <ToggleButton value="hour">Hour</ToggleButton>
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {renderContent()}
      </Stack>
    </Stack>
  );
}

TemperatureChartView.propTypes = {
  temperatureData: PropTypes.shape({
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        timestamp: PropTypes.string,
        open: PropTypes.number,
        close: PropTypes.number,
        min: PropTypes.number,
        max: PropTypes.number,
        trend: PropTypes.string,
      })
    ),
  }),
  rangeType: PropTypes.string.isRequired,
  setRangeType: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

TemperatureChartView.defaultProps = {
  temperatureData: null,
};

function HRVChartView({ hrvData, rangeType, setRangeType, isLoading }) {
  const hasData = hrvData?.data_points?.length > 0;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!hasData) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            margin: "30px 0",
            py: 4,
          }}
        >
          <Typography color="text.secondary">No HRV Data</Typography>
        </Box>
      );
    }

    return <HRVChart hrvData={hrvData} />;
  };

  const handleRangeChange = (_, newValue) => {
    if (newValue) {
      setRangeType(newValue);
    }
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" component="h3">
            Heart Rate Variability
          </Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={rangeType}
            onChange={handleRangeChange}
            sx={{
              "& .MuiToggleButton-root": {
                fontSize: 10,
                px: 1,
                py: 0.5,
                minHeight: 28,
                color: "#666",
                border: "1px solid #E0E0E0",
                "&.Mui-selected": {
                  color: "#1976d2",
                  backgroundColor: "#E3F2FD",
                },
                "&.Mui-focusVisible": {
                  color: "#1976d2",
                },
              },
            }}
          >
            <ToggleButton value="hour">Hour</ToggleButton>
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {renderContent()}
      </Stack>
    </Stack>
  );
}

HRVChartView.propTypes = {
  hrvData: PropTypes.shape({
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        timestamp: PropTypes.string,
        hrv: PropTypes.number,
        bpm: PropTypes.number,
      })
    ),
  }),
  rangeType: PropTypes.string.isRequired,
  setRangeType: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

HRVChartView.defaultProps = {
  hrvData: null,
};

function OuraRingDetail() {
  const { deviceId, hubId } = useParams();
  const history = useHistory();
  const [device, setDevice] = useState({});
  const [heartRateDevices, setHeartRateDevices] = useState(null);
  const [spo2Data, setSpo2Data] = useState(null);
  const [rangeType, setRangeType] = useState("hour");
  const [measures, setMeasures] = useState([]);
  const [totalMeasures, setTotalMeasures] = useState(0);
  const [lastMeasures, setLastMeasures] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSpO2, setIsLoadingSpO2] = useState(false);
  const [temperatureData, setTemperatureData] = useState(null);
  const [isLoadingTemperature, setIsLoadingTemperature] = useState(false);
  const [hrvData, setHrvData] = useState(null);
  const [isLoadingHRV, setIsLoadingHRV] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const { paginationModel, setPaginationModel } = usePagination();
  const startOfToday = useMemo(() => startOfDay(subDays(new Date(), 7)), []);
  const endOfToday = useMemo(() => endOfDay(new Date()), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [hubEsn, setHubEsn] = useState("");
  const [loadingDataRetrieve, setLoadingDataRetrieve] = useState(false);
  const [isStatusStale, setIsStatusStale] = useState(false);
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);
  const statusTimeoutRef = useRef(null);

  const fetchMeasures = useCallback(
    async (macAddress, boundHub) => {
      setIsLoading(true);
      try {
        const res = await get("/measures", {
          device_mac: macAddress,
          taken_after: startOfToday.toISOString(),
          taken_before: endOfToday.toISOString(),
          limit: tabIndex === 1 ? paginationModel.pageSize : LIMITDEFAULT,
          offset: tabIndex === 1 ? paginationModel.page : 0,
          esn: boundHub,
        });

        if (res.status === 200) {
          setMeasures(res.data.measures);
          setTotalMeasures(res.data.total_count);
        } else {
          enqueueSnackbar("Unable to retrieve measurements", {
            variant: "error",
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [startOfToday, endOfToday, paginationModel, tabIndex]
  );

  const fetchHeartRate = useCallback(async () => {
    const heartRateRes = await get(
      `internal/oura/devices/${deviceId}/heart-rate-data`,
      {
        range_type: rangeType,
      }
    );
    if (heartRateRes.status === 200) {
      setHeartRateDevices(heartRateRes.data);
    } else {
      enqueueSnackbar("Unable to retrieve live hr device", {
        variant: "error",
      });
    }
  }, [deviceId, rangeType]);

  const fetchSpO2 = useCallback(async () => {
    setIsLoadingSpO2(true);
    try {
      const spo2Res = await get(`internal/oura/devices/${deviceId}/spo2-data`, {
        range_type: rangeType,
      });
      if (spo2Res.status === 200) {
        setSpo2Data(spo2Res.data);
      } else {
        enqueueSnackbar("Unable to retrieve SpO2 data", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error fetching SpO2 data", {
        variant: "error",
      });
    } finally {
      setIsLoadingSpO2(false);
    }
  }, [deviceId, rangeType]);

  const fetchTemperature = useCallback(async () => {
    setIsLoadingTemperature(true);
    try {
      const tempRes = await get(
        `internal/oura/devices/${deviceId}/temperature-data`,
        {
          range_type: rangeType,
        }
      );
      if (tempRes.status === 200) {
        setTemperatureData(tempRes.data);
      } else {
        enqueueSnackbar("Unable to retrieve temperature data", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error fetching temperature data", {
        variant: "error",
      });
    } finally {
      setIsLoadingTemperature(false);
    }
  }, [deviceId, rangeType]);

  const fetchHRV = useCallback(async () => {
    setIsLoadingHRV(true);
    try {
      const hrvRes = await get(`internal/oura/devices/${deviceId}/hrv-data`, {
        range_type: rangeType,
      });
      if (hrvRes.status === 200) {
        setHrvData(hrvRes.data);
      } else {
        enqueueSnackbar("Unable to retrieve HRV data", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error fetching HRV data", {
        variant: "error",
      });
    } finally {
      setIsLoadingHRV(false);
    }
  }, [deviceId, rangeType]);

  const fetchOuraDevices = useCallback(
    async (hubId) => {
      setIsLoading(true);
      try {
        const res = await get("/oura/devices", { hub_id: hubId });
        if (res.status === 200) {
          const device = (res?.data?.devices || []).find(
            (item) => item.id === deviceId
          );
          setLastMeasures(device);
        } else {
          enqueueSnackbar("Unable to retrieve oura devices", {
            variant: "error",
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [deviceId]
  );

  const fetchOuraSession = useCallback(async () => {
    const res = await get(`hubs/${hubId}/oura/session`);

    if (res.status !== 200) {
      setIsSyncing(false);
      return;
    }

    setHubEsn(res.data.hub_esn);
    setIsSyncing(res.data.status === "active");
  }, [hubId]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleBackClick = () => {
    if (!hubId) return;
    history.push(`/hubs/${hubId}/oura-devices`);
  };

  const onDataRetrieval = async () => {
    setLoadingDataRetrieve(true);
    const res = await post(`hubs/${hubId}/oura/data-retrieval`);
    if (res.status !== 200) {
      enqueueSnackbar("Unable to sync hub device", { variant: "error" });
    } else {
      enqueueSnackbar("Hub device sync initiated successfully", {
        variant: "success",
      });
    }
    setLoadingDataRetrieve(false);
  };

  const refreshRingStatus = useCallback(async () => {
    if (!hubId || !deviceId) return;

    setIsRefreshingStatus(true);
    setIsStatusStale(false);

    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);

    try {
      const commandRes = await post(`hubs/${hubId}/oura/status`);
      if (commandRes.status !== 200) {
        setIsStatusStale(true);
        setIsRefreshingStatus(false);
        return;
      }
      // Status update will arrive via WebSocket → latestStatusMessage useEffect handles it.
      // Fall back to stale after 10 s if hub doesn't respond.
      statusTimeoutRef.current = setTimeout(() => {
        setIsStatusStale(true);
        setIsRefreshingStatus(false);
      }, 10000);
    } catch (error) {
      setIsStatusStale(true);
      setIsRefreshingStatus(false);
    }
  }, [hubId, deviceId]);

  const dataGridColumns = [
    {
      field: "timestamp",
      headerName: "Time",
      ...(isMobile ? { width: 200 } : { flex: 0.8 }),
      valueGetter: (params) => new Date(params).toLocaleString(),
    },
    {
      field: "value",
      headerName: "Measure Value",
      ...(isMobile ? { width: 280 } : { flex: 1.2 }),
      sortable: false,
      renderCell: (params) => (
        <Box>
          {params.row.value?.bpm} bpm
          <Typography color="#E6E6EC" sx={{ px: 1 }} component="span">
            |
          </Typography>
          {params.row.value?.spo2} %
          <Typography color="#E6E6EC" sx={{ px: 1 }} component="span">
            |
          </Typography>
          {params.row.value?.temp || params.row.value?.temp_central} °C
          <Typography color="#E6E6EC" sx={{ px: 1 }} component="span">
            |
          </Typography>
          {params.row.value?.hrv} ms HRV
        </Box>
      ),
    },
    {
      field: "hub_id",
      headerName: "Hub ID",
      ...(isMobile ? { width: 120 } : { flex: 0.7 }),
      renderCell: (params) => params.row.hub?.hub_id,
      sortable: false,
    },
    {
      field: "device_make",
      headerName: "Device Make",
      ...(isMobile ? { width: 150 } : { flex: 0.8 }),
      renderCell: (params) => params.row.device?.make,
      sortable: false,
    },
    {
      field: "device_model",
      headerName: "Device Model",
      ...(isMobile ? { width: 150 } : { flex: 0.8 }),
      renderCell: (params) => params.row.device?.model_display_name,
      sortable: false,
    },
    {
      field: "device_mac",
      headerName: "MAC Address",
      sortable: false,
      renderCell: (params) => {
        const { device } = params.row;
        const make = device?.make;
        const model = device?.model_display_name;
        const title = make && model ? `${make} ${model}` : "N/A";
        return (
          <Tooltip
            title={<Box sx={{ fontSize: 13 }}>{title}</Box>}
            placement="right"
            arrow
          >
            <span>{device?.mac_address}</span>
          </Tooltip>
        );
      },
      ...(isMobile ? { width: 170 } : { flex: 0.8 }),
    },
  ];

  useEffect(() => {
    if (hubId) {
      fetchOuraSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubId]);

  useEffect(() => {
    if (device?.mac_address) {
      const debouncedFetch = debounce(
        () => fetchMeasures(device.mac_address, device.bound_hub),
        300
      );
      debouncedFetch();
      return () => debouncedFetch.cancel();
    }
    return undefined;
  }, [device, fetchMeasures]);

  useEffect(() => {
    if (device?.bound_hub) {
      const debouncedFetch = debounce(
        () => fetchOuraDevices(device.bound_hub),
        300
      );
      debouncedFetch();
      return () => debouncedFetch.cancel();
    }
    return undefined;
  }, [device, fetchOuraDevices]);

  useEffect(() => {
    const fetchDevice = async () => {
      const deviceRes = await get(`/devices/${deviceId}`);
      if (deviceRes.status === 200) {
        setDevice(deviceRes.data);
      } else {
        enqueueSnackbar("Unable to retrieve device details", {
          variant: "error",
        });
      }
    };
    fetchDevice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  useEffect(() => {
    if (hubId && deviceId) {
      refreshRingStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubId, deviceId]);

  useEffect(() => {
    fetchHeartRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, rangeType]);

  useEffect(() => {
    fetchSpO2();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, rangeType]);

  useEffect(() => {
    fetchTemperature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, rangeType]);

  useEffect(() => {
    fetchHRV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, rangeType]);

  // ---------------------------------------------------------------------------
  // Real-time WebSocket updates — replace the live-HR polling interval
  // ---------------------------------------------------------------------------
  const { latestMessage: latestHrMessage } = useOuraRealtime(deviceId, "hr");
  const { latestMessage: latestSpo2Message } = useOuraRealtime(
    deviceId,
    "spo2"
  );
  const { latestMessage: latestTempMessage } = useOuraRealtime(
    deviceId,
    "temp"
  );
  const { latestMessage: latestHrvMessage } = useOuraRealtime(deviceId, "hrv");
  const { latestMessage: latestStatusMessage } = useOuraRealtime(
    deviceId,
    "status"
  );

  const updateMeasureStat = useCallback((statsKey, currentValue, timestamp) => {
    setLastMeasures((prev) =>
      prev
        ? {
            ...prev,
            [statsKey]: {
              ...prev[statsKey],
              current: currentValue,
              last_sync: timestamp,
            },
          }
        : prev
    );
  }, []);

  useEffect(() => {
    if (!latestHrMessage) return;
    updateMeasureStat(
      "hr_statistics",
      latestHrMessage.bpm,
      latestHrMessage.timestamp
    );
  }, [latestHrMessage, updateMeasureStat]);

  useEffect(() => {
    if (!latestSpo2Message) return;
    updateMeasureStat(
      "spo2_statistics",
      latestSpo2Message.spo2,
      latestSpo2Message.timestamp
    );
  }, [latestSpo2Message, updateMeasureStat]);

  useEffect(() => {
    if (!latestTempMessage) return;
    updateMeasureStat(
      "temperature_statistics",
      {
        central: latestTempMessage.temp_central,
        distal: latestTempMessage.temp_distal,
        proximal: latestTempMessage.temp_proximal,
      },
      latestTempMessage.timestamp
    );
  }, [latestTempMessage, updateMeasureStat]);

  useEffect(() => {
    if (!latestHrvMessage) return;
    updateMeasureStat(
      "hrv_statistics",
      latestHrvMessage.hrv,
      latestHrvMessage.timestamp
    );
  }, [latestHrvMessage, updateMeasureStat]);

  useEffect(() => {
    if (!latestStatusMessage) return;
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    setIsRefreshingStatus(false);
    setIsStatusStale(false);
    setDevice((prev) => ({
      ...prev,
      connection_status: {
        ...prev.connection_status,
        ...getConnectionStatus(latestStatusMessage.connection_status),
      },
      wear_status: getWearingStatus(latestStatusMessage.wear_state),
      battery_level: latestStatusMessage.battery_level,
    }));
  }, [latestStatusMessage, deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainContentContainer
      title={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          flexWrap="wrap"
        >
          <Box display="flex" alignItems="center">
            <Button
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "100%",
                minWidth: 0,
              }}
              onClick={handleBackClick}
            >
              <ChevronLeftIcon sx={{ color: "#1F79E2" }} />
            </Button>
            <Typography
              component="h1"
              sx={{ fontSize: 24, fontWeight: 700, margin: 0 }}
            >
              {device.mac_address}
            </Typography>
          </Box>
          <Button
            variant="contained"
            sx={{ textTransform: "uppercase", fontSize: 12, fontWeight: 600 }}
            onClick={onDataRetrieval}
            startIcon={<Heart />}
            loading={loadingDataRetrieve}
          >
            Data retrieval
          </Button>
        </Box>
      }
      analyticsTitle={`${device.make} ${device.model}`}
    >
      <Stack spacing={3}>
        <DeviceHeader
          device={device}
          isStatusStale={isStatusStale}
          isRefreshingStatus={isRefreshingStatus}
        />

        <Grid
          sx={{
            borderRadius: 2,
            border: "1px solid rgb(224, 224, 224)",
            p: 2,
          }}
        >
          <MetricsSection
            lastMeasures={lastMeasures}
            setIsSyncing={setIsSyncing}
            isSyncing={isSyncing}
            hubEsn={hubEsn}
          />
          <Tabs
            sx={{
              borderRadius: 8,
              p: "6px",
              backgroundColor: "#F9F9F9",
              "& .MuiTabs-indicator": { backgroundColor: "transparent" },
              "& .MuiStack-root": { display: "none !important" },
              minHeight: 32,
              width: { xs: "auto", sm: "fit-content", lg: "fit-content" },
              mb: 2,
            }}
            value={tabIndex}
            onChange={handleTabChange}
          >
            {TABS.map((item, index) => (
              <Tab
                key={item.label}
                sx={{
                  width: { xs: "50%", sm: 150, lg: 150 },
                  minHeight: 30,
                  borderRadius: 8,
                  fontWeight: 500,
                  "&.Mui-selected": {
                    color: "#1F79E2",
                    backgroundColor: "white",
                    fontWeight: 500,
                  },
                  padding: 1,
                  textTransform: "uppercase",
                }}
                label={item.label}
                value={index}
              />
            ))}
          </Tabs>

          <TabPanel value={tabIndex} index={0}>
            <HeartRateChartView
              heartRateDevices={heartRateDevices}
              setRangeType={setRangeType}
              rangeType={rangeType}
              isLoading={isLoading}
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={1}>
            <SpO2ChartView
              spo2Data={spo2Data}
              setRangeType={setRangeType}
              rangeType={rangeType}
              isLoading={isLoadingSpO2}
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={2}>
            <TemperatureChartView
              temperatureData={temperatureData}
              setRangeType={setRangeType}
              rangeType={rangeType}
              isLoading={isLoadingTemperature}
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={3}>
            <HRVChartView
              hrvData={hrvData}
              setRangeType={setRangeType}
              rangeType={rangeType}
              isLoading={isLoadingHRV}
            />
          </TabPanel>

          <TabPanel value={tabIndex} index={4}>
            <StelDataGrid
              loading={isLoading}
              columns={dataGridColumns}
              rows={measures}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              paginationMode="client"
              rowCount={totalMeasures}
              sx={{
                "& .MuiDataGrid-columnHeader": { backgroundColor: "#F1F1F1" },
                "& .MuiDataGrid-columnSeparator": { display: "none" },
              }}
            />
          </TabPanel>
        </Grid>
      </Stack>
    </MainContentContainer>
  );
}

export default OuraRingDetail;
